import React from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCurrentCompany } from 'hooks'
import { useSetMemberActive } from 'hooks/api'

import { Stack, Typography, Box } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { UserBlock } from 'components'

interface ArchiveCompanyUserModalProps {
  user: any
  open: boolean
  onClose: () => void
  refetch: () => void
  /** true = reactivate flow (archived user), false = archive flow. */
  reactivate?: boolean
}

/**
 * Soft, reversible archive / reactivate for a company member (VERA-816).
 * Flips company_users.active. Distinct from DeleteCompanyUserModal (hard delete).
 */
export const ArchiveCompanyUserModal = (props: ArchiveCompanyUserModalProps) => {
  const { user, open, onClose = () => {}, refetch, reactivate = false } = props
  const { open: notification } = useSnackbar()
  const { company } = useCurrentCompany()
  const name = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.email

  const { mutate: setActive, isPending } = useSetMemberActive({
    onSuccess: () => {
      onClose()
      refetch()
    },
    onError: (error: any) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleConfirm = () => {
    if (!user?.id || !company?.id) {
      notification({ type: 'error', title: 'Cannot update user: missing user or company' })
      return
    }
    setActive({ companyId: company.id, userId: user.id, active: reactivate })
  }

  return (
    <Modal
      title={reactivate ? 'Reactivate user' : 'Archive user'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button disableElevation loading={isPending} onClick={handleConfirm}>
            {reactivate ? 'Reactivate' : 'Archive'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px" mb={6}>
        <Box sx={{ p: 3, borderRadius: 2, border: theme => `1px solid ${theme.palette.divider}` }}>
          <UserBlock user={user} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {reactivate
            ? `Reactivate "${name}"? They'll regain access and reappear in the active users list.`
            : `Archive "${name}"? They'll be set inactive and lose access, but their record is kept. You can reactivate them anytime via the "Show archived" toggle above the users list. To remove them permanently, use Delete instead.`}
        </Typography>
      </Stack>
    </Modal>
  )
}
